import { DatePipe } from '@angular/common';
import { Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';

import { SystemsQuery } from 'src/app/systems/state/systems.query';
import { ContentPipe } from 'src/app/services/content/content.pipe';
import { HtmlDecodePipe } from 'src/app/shared/pipes/html-decode.pipe';
import { RiskRegistryColumnEnum } from 'src/app/api/models/risks/risks.interface';
import { CollaboratorsChipPipe } from 'src/app/ai-assessments/pipes/collaborators-chip.pipe';
import { AiAssessmentTemplateNamePipe } from 'src/app/ai-assessments/pipes/ai-assessment-template-name.pipe';
import { AiAssessmentLabelsPipe } from 'src/app/ai-assessments/pipes/ai-assessment-labels.pipe';
import { FeatureFlagQuery } from 'src/app/feature-flag/state/feature-flag.query';
import { FeatureFlags } from 'src/app/api/models/profile/profile-feature-flags.enum';
import { RiskCalulationToTextPipe } from 'src/app/ai-assessments/pipes/risk-calculation-to-text.pipe';
import { AssessmentsService } from 'src/app/ai-assessments/services/assessments.service';
import { AssessmentQuestionTypeEnum } from 'src/app/api/models/assessments/assessment.enum';
import { AssessmentPage } from 'src/app/ai-assessments/models/assessments.interface';
import { Risk } from '../models/risks.interface';

@Injectable()
export class RiskRegistryTableCsvHelper {

  constructor(
    private datePipe: DatePipe,
    private contentPipe: ContentPipe,
    private systemsQuery: SystemsQuery,
    private htmlDecodePipe: HtmlDecodePipe,
    private riskCalulationToTextPipe: RiskCalulationToTextPipe,
    private collaboratorsChipPipe: CollaboratorsChipPipe,
    private aiAssessmentTemplateNamePipe: AiAssessmentTemplateNamePipe,
    private aiAssessmentLabelsPipe: AiAssessmentLabelsPipe,
    private featureFlagQuery: FeatureFlagQuery,
    private assessmentsService: AssessmentsService
  ) {}

  async getValueByKey(risk: Risk, key: RiskRegistryColumnEnum | string): Promise<string> {
    const values: Record<RiskRegistryColumnEnum, string> = {
      name: risk.name ? this.htmlDecodePipe.transform(risk.name) : '',
      createdDate: risk.createdDate ? this.datePipe.transform(risk.createdDate, 'dd MMM, yyyy') : '-',
      assessmentType: risk.assessmentPage ? this.aiAssessmentTemplateNamePipe.transform((risk?.assessmentPage?.['type'] ?? risk?.assessmentPage?.['templateType'])) : '-',
      collaborators: await this.getCollaborators(risk),
      inherentRisk: this.riskCalulationToTextPipe.transform(risk.inherentRisk) ?? '-',
      residualRisk: this.riskCalulationToTextPipe.transform(risk.residualRisk) ?? '-',
      likelihood: this.riskCalulationToTextPipe.transform(risk.likelihood) ?? '-',
      severity: this.riskCalulationToTextPipe.transform(risk.severity) ?? '-',
      comment: this.getComment(risk),
      accepted: this.contentPipe.transform(`common.${risk.accepted ? 'yes' : 'no'}`),
      mitigations: risk.mitigations?.length ? risk.mitigations.map(mitigation => this.htmlDecodePipe.transform(mitigation)).join(', ') : '-',
      relatedAssessmentId: risk.assessmentPage?.name ? this.htmlDecodePipe.transform(risk.assessmentPage.name) : '-',
      relatedEntityIds: this.getDataSources(risk),
      labels: await this.getLabels(risk),
    };
    if (key in values) {
      return values[key];
    }
    return '-';
  }

  private async getLabels(risk: Risk): Promise<string> {
    // DevRiskLabels: the risk's own labels (BE returns names) instead of the source assessment's.
    if (this.featureFlagQuery.getFlag(FeatureFlags.DevRiskLabels)) {
      const names = (risk.tags ?? []).map(tag => tag.name).filter(Boolean);
      return names.length ? names.join(', ') : '-';
    }

    if(risk.assessmentPage?.tags) {
      const { labels } = await firstValueFrom(this.aiAssessmentLabelsPipe.transform(risk.assessmentPage?.tags));
      if (labels) {
        return labels?.join(', ');
      }
    }
    return '-';
  }
  
  private async getCollaborators(risk: Risk): Promise<string> {
    const collaborators = await this.collaboratorsChipPipe.transform(risk.assessmentPage);
    if (collaborators) {
      return collaborators.text ?? '-';
    }
    return '-';
  }
  
  private getDataSources(risk: Risk): string {
    // Same source of truth as the on-screen table (see RelatedEntitiesArrayPipe /
    // relatedEntitiesToTableEntities): data source system ids come from the assessment's
    // vendorSpecificId (vendor assessments) or from its DataSources-type question value,
    // not from `externalRelatedEntities`/`relatedEntities`, which don't exist on AssessmentPage.
    const systemIds = this.assessmentsService.getRelatedEntitiesByAssessment(
      risk.assessmentPage as AssessmentPage,
      AssessmentQuestionTypeEnum.DataSources
    );

    if (Array.isArray(systemIds) && systemIds.length) {
      const names = systemIds
        .map(systemId => this.systemsQuery.getEntity(systemId)?.name)
        .filter(Boolean)
        .map(name => this.htmlDecodePipe.transform(name));
      return names.length ? names.join(', ') : '-';
    }
    return '-';
  }

  private getComment(risk: Risk): string {
    if (!risk.comment) return '-';
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = risk.comment;
    return tempDiv.textContent || tempDiv.innerText || '-';
  }
}